import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddAPhoto as AddPhotoIcon,
  Delete as DeleteIcon,
  DirectionsCar as TransportIcon,
  Flight as FlightIcon,
  FreeBreakfast as FreeTimeIcon,
  Hotel as HotelIcon,
  LocalActivity as ActivityIcon,
  MoreHoriz as OtherIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChangeHistoryPanel from '../components/ChangeHistoryPanel';
import ActivityModal from '../components/ActivityModal';
import { MultiImageUploader, SingleImageUploader } from '../components/ImageUploader';
import LocationAutocomplete from '../components/LocationAutocomplete';
import PartnerDmcField from '../components/PartnerDmcField';
import PartnerPlanWall from '../components/PartnerPlanWall';
import PartnerInventoryPickerDialog from '../components/PartnerInventoryPickerDialog';
import {
  PartnerPageShell,
  partnerLinkSx,
} from '../components/PartnerListChrome';
import api from '../config/api';
import { computeIntroductionFeeAmount, introductionFeeCopyVars } from '../config/plans';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import usePrivateTravelAvailable from '../hooks/usePrivateTravelAvailable';
import { CANCELLATION_POLICY_KEYS, DEFAULT_CANCELLATION_POLICY } from '../constants/cancellationPolicies';
import { DOCUMENT, documentFormLabelSx, documentSubSectionTitleSx } from '../constants/documentTheme';
import { CURRENCIES } from '../utils/currencyUtils';
import { isIncludedPackageItem } from '../utils/packageItems';
import { normalizeInventoryPriceFrequency } from '../utils/inventoryPrice';
import { isPartnerPlanLimitError, canUseMarketplaceListing, canUseFeaturedPlacement } from '../utils/partnerPlan';

const PACKAGE_TYPES = [
  { value: 'beach' },
  { value: 'adventure' },
  { value: 'city' },
  { value: 'cultural' },
  { value: 'cruise' },
  { value: 'safari' },
  { value: 'ski' },
  { value: 'wellness' },
  { value: 'honeymoon' },
  { value: 'family' },
  { value: 'luxury' },
  { value: 'budget' },
  { value: 'custom' },
];

const PRICING_TYPES = [
  { value: 'land_only', hintKey: 'styleHintLand' },
  { value: 'flight_land', hintKey: 'styleHintFlights' },
  { value: 'all_inclusive', hintKey: 'styleHintAll' },
];

const ITEM_CATEGORIES = [
  { value: 'flight', icon: <FlightIcon /> },
  { value: 'accommodation', icon: <HotelIcon /> },
  { value: 'activity', icon: <ActivityIcon /> },
  { value: 'restaurant', icon: <RestaurantIcon /> },
  { value: 'transport', icon: <TransportIcon /> },
  { value: 'free_time', icon: <FreeTimeIcon /> },
  { value: 'other', icon: <OtherIcon /> },
];

const ROOM_TYPES = ['single', 'double', 'triple', 'quad', 'group'];
const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'AED'];
const SHORT_MAX = 500;
const REVIEW_STEP = 4;
const ADDONS_STEP = 3;

const isIncludedItem = isIncludedPackageItem;

const PARTNER_AUTH = { usePartnerAuth: true };

const inventoryCategoryToPackage = (category) => {
  if (category === 'restaurant') return 'restaurant';
  if (category === 'car') return 'transport';
  if (category === 'villa') return 'hotel';
  return 'activity';
};

const underlineSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputBase-root': { pl: 0, pr: 0, width: '100%' },
  '& .MuiInputBase-input': {
    fontSize: 16,
    py: 1.25,
    pl: 0,
    pr: 0,
    color: DOCUMENT.ink,
    lineHeight: 1.5,
    overflow: 'visible',
  },
  '& .MuiInputBase-input::placeholder': { color: DOCUMENT.placeholder, opacity: 1 },
  '& .MuiSelect-select': { fontSize: 16, py: 1.25, color: DOCUMENT.ink },
};

const locationSx = underlineSx;

const ghostSx = {
  background: 'none',
  border: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  cursor: 'pointer',
  p: 0,
  pb: '2px',
  borderBottom: '1px solid transparent',
  '&:hover': { color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
};

const parseList = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (!value) return [];
  return String(value)
    .split(/\r?\n|·|;/)
    .map((item) => item.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean);
};

const joinList = (items) => items.map((item) => String(item).trim()).filter(Boolean).join('\n');

const firstTierPrice = (tiers) => {
  const priced = tiers.find((tier) => parseFloat(tier.price_per_person) > 0);
  return priced ? parseFloat(priced.price_per_person) : 0;
};

const formatMoney = (amount, currency = 'USD') => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `$${value.toLocaleString()}`;
  }
};

const toNumberOr = (value, fallback = null) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const DEFAULT_PRICING_TIERS = [
  { tier_name: 'Standard', description: '', price_per_person: '', occupancy_type: 'double', stripe_payment_url: '' },
];

const getDraftSnapshot = (form, packageItems, tiers) => JSON.stringify({
  form,
  items: packageItems,
  pricingTiers: tiers,
});

const emptyForm = {
  title: '',
  trip_code: '',
  slug: '',
  description: '',
  short_description: '',
  destination: '',
  destination_country: '',
  package_type: 'custom',
  pricing_type: 'land_only',
  start_location: '',
  end_location: '',
  duration_days: 7,
  duration_nights: 6,
  base_price: '',
  currency: 'USD',
  original_price: '',
  discount_percent: '',
  discount_valid_until: '',
  is_featured: false,
  is_private_journey: false,
  pricing_kind: '',
  budget_min: '',
  budget_max: '',
  pricing_assumptions: '',
  price_includes: '',
  price_excludes: '',
  available_from: '',
  available_until: '',
  max_travelers: 20,
  min_travelers: 1,
  booking_deadline_days: 7,
  cancellation_policy: DEFAULT_CANCELLATION_POLICY,
  is_customizable: true,
  customization_notes: '',
  cover_image: '',
  gallery: [],
  meta_title: '',
  meta_description: '',
  tags: [],
  booking_method: 'checkout',
  dmc_id: null,
  visibility: 'marketplace',
  external_booking_url: '',
  stripe_payment_url: '',
};

const PartnerPackageEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const [savedPackageId, setSavedPackageId] = useState(packageId || null);
  const activePackageId = packageId || savedPackageId;
  const isEditing = Boolean(packageId || savedPackageId);

  const [activeStep, setActiveStep] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [itemEditorMode, setItemEditorMode] = useState('included');
  const [addOnDay, setAddOnDay] = useState(1);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryId, setInventoryId] = useState('');
  const [inventoryBusy, setInventoryBusy] = useState(false);
  const [inventoryPricingMode, setInventoryPricingMode] = useState('charge');
  const [loading, setLoading] = useState(Boolean(packageId || duplicateId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [previewKey, setPreviewKey] = useState(0);
  const [previewSlug, setPreviewSlug] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(-1);
  const [includeDraft, setIncludeDraft] = useState('');
  const [excludeDraft, setExcludeDraft] = useState('');
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planWallReason, setPlanWallReason] = useState('billing');
  const [planEntitlements, setPlanEntitlements] = useState(null);
  const { entitlements, introductionFeeRate } = usePartnerEntitlements();
  const { available: privateTravelAvailable } = usePrivateTravelAvailable();
  const [formData, setFormData] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [pricingTiers, setPricingTiers] = useState(DEFAULT_PRICING_TIERS);
  const [savedSnapshot, setSavedSnapshot] = useState(() => (
    getDraftSnapshot(emptyForm, [], DEFAULT_PRICING_TIERS)
  ));
  const savingRef = useRef(false);
  const currentSnapshot = useMemo(
    () => getDraftSnapshot(formData, items, pricingTiers),
    [formData, items, pricingTiers]
  );
  const isDirty = currentSnapshot !== savedSnapshot;

  const includes = useMemo(() => parseList(formData.price_includes), [formData.price_includes]);
  const excludes = useMemo(() => parseList(formData.price_excludes), [formData.price_excludes]);
  const days = Math.max(1, parseInt(formData.duration_days, 10) || 1);
  const nights = Math.max(0, parseInt(formData.duration_nights, 10) || 0);
  const price = firstTierPrice(pricingTiers) || parseFloat(formData.base_price) || 0;
  const effectivePreviewSlug = previewSlug || formData.slug;
  const basicsMissing = (!formData.title.trim() ? 1 : 0) + (!formData.destination.trim() ? 1 : 0);
  const includedItems = useMemo(() => items.filter(isIncludedItem), [items]);
  const addOnItems = useMemo(() => items.filter((item) => !isIncludedItem(item)), [items]);
  const ready = Boolean(
    formData.title.trim()
    && includedItems.length > 0
    && (formData.base_price || price > 0)
    && !(formData.booking_method === 'external' && !formData.external_booking_url.trim())
  );
  const sampleBooking = price > 0 ? price : 2400;
  // The fee steps down by tier, so the worked example has to use this partner's
  // own rate rather than the middle of the ladder.
  const feeRate = introductionFeeRate;
  const feeCopy = introductionFeeCopyVars();
  const youReceive = feeRate == null
    ? null
    : Math.round((sampleBooking - computeIntroductionFeeAmount(sampleBooking, feeRate)) * 100) / 100;

  const itemsByDay = useMemo(() => {
    const grouped = {};
    for (let day = 1; day <= days; day += 1) {
      grouped[day] = includedItems
        .filter((item) => item.day_number === day)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    return grouped;
  }, [days, includedItems]);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('partnerToken')}`,
  }), []);

  useEffect(() => {
    if (!localStorage.getItem('partnerToken')) {
      navigate('/partner/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (packageId) setSavedPackageId(packageId);
  }, [packageId]);

  const fetchPackage = useCallback(async (id, isDuplicate = false) => {
    try {
      setLoading(true);
      const response = await api.get(`/partner-packages/packages/${id}`, {
        headers: getAuthHeaders(),
      });
      const pkg = response.data.package;
      if (!isDuplicate && pkg.slug) {
        setPreviewSlug(pkg.slug);
      }
      const nextForm = {
        ...emptyForm,
        title: isDuplicate ? `${pkg.title} (Copy)` : pkg.title,
        trip_code: isDuplicate ? '' : (pkg.trip_code || ''),
        slug: isDuplicate ? '' : (pkg.slug || ''),
        description: pkg.description || '',
        short_description: pkg.short_description || '',
        destination: pkg.destination || '',
        destination_country: pkg.destination_country || '',
        package_type: pkg.package_type || 'custom',
        pricing_type: pkg.pricing_type || 'land_only',
        start_location: pkg.start_location || '',
        end_location: pkg.end_location || '',
        duration_days: pkg.duration_days || 7,
        duration_nights: pkg.duration_nights || Math.max(0, (pkg.duration_days || 7) - 1),
        base_price: pkg.base_price || '',
        currency: pkg.currency || 'USD',
        original_price: pkg.original_price || '',
        discount_percent: pkg.discount_percent || '',
        discount_valid_until: pkg.discount_valid_until ? String(pkg.discount_valid_until).split('T')[0] : '',
        is_featured: pkg.is_featured || false,
        is_private_journey: Boolean(pkg.is_private_journey),
        pricing_kind: pkg.pricing_kind || (pkg.is_private_journey ? 'indicative_group_budget' : ''),
        budget_min: pkg.budget_min != null ? pkg.budget_min : '',
        budget_max: pkg.budget_max != null ? pkg.budget_max : '',
        pricing_assumptions: pkg.pricing_assumptions || '',
        price_includes: pkg.price_includes || '',
        price_excludes: pkg.price_excludes || '',
        available_from: pkg.available_from ? String(pkg.available_from).split('T')[0] : '',
        available_until: pkg.available_until ? String(pkg.available_until).split('T')[0] : '',
        max_travelers: pkg.max_travelers || 20,
        min_travelers: pkg.min_travelers || 1,
        booking_deadline_days: pkg.booking_deadline_days ?? 7,
        cancellation_policy: pkg.cancellation_policy || DEFAULT_CANCELLATION_POLICY,
        is_customizable: pkg.is_customizable !== false,
        customization_notes: pkg.customization_notes || '',
        cover_image: pkg.cover_image || '',
        gallery: Array.isArray(pkg.gallery)
          ? pkg.gallery
          : (typeof pkg.gallery === 'string' ? JSON.parse(pkg.gallery || '[]') : []),
        meta_title: pkg.meta_title || '',
        meta_description: pkg.meta_description || '',
        tags: pkg.tags || [],
        booking_method: pkg.booking_method || 'checkout',
        dmc_id: pkg.dmc_id || null,
        visibility: pkg.visibility === 'private_client' ? 'private_client' : 'marketplace',
        external_booking_url: pkg.external_booking_url || '',
        stripe_payment_url: pkg.stripe_payment_url || '',
      };
      const nextItems = pkg.items || [];
      const nextTiers = pkg.pricing_tiers?.length > 0
        ? pkg.pricing_tiers.map((tier) => ({
          ...tier,
          stripe_payment_url: tier.stripe_payment_url || '',
        }))
        : DEFAULT_PRICING_TIERS;
      setFormData(nextForm);
      setItems(nextItems);
      setPricingTiers(nextTiers);
      setSavedSnapshot(getDraftSnapshot(nextForm, nextItems, nextTiers));
    } catch (err) {
      console.error('Error fetching package:', err);
      setError(t('createPackage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => {
    if (packageId) {
      fetchPackage(packageId);
    } else if (duplicateId) {
      fetchPackage(duplicateId, true);
    }
  }, [packageId, duplicateId, fetchPackage]);

  useEffect(() => {
    if (packageId || duplicateId || !entitlements) return;
    if (!canUseMarketplaceListing(entitlements)) {
      setFormData((prev) => (
        prev.visibility === 'private_client' ? prev : { ...prev, visibility: 'private_client' }
      ));
    }
  }, [entitlements, packageId, duplicateId]);

  useEffect(() => {
    if (packageId || duplicateId || entitlements?.tier !== 'bespoke') return;
    setFormData((prev) => (
      prev.visibility === 'private_client' ? prev : { ...prev, visibility: 'private_client' }
    ));
  }, [entitlements?.tier, packageId, duplicateId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'duration_days') {
        const nextDays = Math.max(1, parseInt(value, 10) || 1);
        next.duration_days = nextDays;
        next.duration_nights = Math.max(0, nextDays - 1);
      }
      if (name === 'short_description') {
        next.short_description = value.slice(0, SHORT_MAX);
      }
      return next;
    });
  };

  const setList = (key, list) => {
    setFormData((prev) => ({ ...prev, [key]: joinList(list) }));
  };

  const addListItem = (key, draft, setDraft) => {
    const value = draft.trim();
    if (!value) return;
    const current = key === 'price_includes' ? includes : excludes;
    setList(key, [...current, value]);
    setDraft('');
  };

  const handleSave = useCallback(async (publish = false) => {
    if (savingRef.current) return null;
    savingRef.current = true;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      setSaveState('saving');

      const payload = {
        ...formData,
        title: formData.title.trim() || t('createPackage.untitled'),
        destination: formData.destination.trim(),
        base_price: toNumberOr(formData.base_price, 0),
        original_price: toNumberOr(formData.original_price, null),
        discount_percent: toNumberOr(formData.discount_percent, null),
        available_from: formData.available_from || null,
        available_until: formData.available_until || null,
        discount_valid_until: formData.discount_valid_until || null,
        budget_min: formData.is_private_journey ? toNumberOr(formData.budget_min, null) : null,
        budget_max: formData.is_private_journey ? toNumberOr(formData.budget_max, null) : null,
        pricing_kind: formData.is_private_journey
          ? (formData.pricing_kind || 'indicative_group_budget')
          : null,
        pricing_assumptions: formData.is_private_journey ? (formData.pricing_assumptions || null) : null,
        items,
        pricing_tiers: pricingTiers.map((tier) => ({
          ...tier,
          price_per_person: toNumberOr(tier.price_per_person, toNumberOr(formData.base_price, 0)),
        })),
      };

      const response = activePackageId
        ? await api.put(`/partner-packages/packages/${activePackageId}`, payload, { headers: getAuthHeaders() })
        : await api.post('/partner-packages/packages', payload, { headers: getAuthHeaders() });

      const savedPackage = response.data.package;
      const nextPackageId = savedPackage?.id || activePackageId;
      const savedSlug = savedPackage?.slug || formData.slug;
      if (savedSlug) setPreviewSlug(savedSlug);
      if (nextPackageId) setSavedPackageId(nextPackageId);
      const nextForm = savedPackage
        ? {
          ...formData,
          trip_code: savedPackage.trip_code || formData.trip_code,
          slug: savedPackage.slug || formData.slug,
        }
        : formData;
      if (savedPackage) {
        setFormData(nextForm);
      }
      setSavedSnapshot(getDraftSnapshot(nextForm, items, pricingTiers));

      if (publish && nextPackageId) {
        await api.post(`/partner-packages/packages/${nextPackageId}/publish`, {}, { headers: getAuthHeaders() });
        setSuccess(t('createPackage.publishedSuccess'));
        setSaveState('saved');
        setTimeout(() => navigate('/partner/packages'), 1500);
      } else {
        setSuccess(t('createPackage.draftSavedSuccess'));
        setSaveState('saved');
        if (!packageId && nextPackageId) {
          navigate(`/partner/packages/${nextPackageId}/edit`, { replace: true });
        }
        setTimeout(() => setSuccess(''), 3000);
      }

      return { savedPackageId: nextPackageId, savedSlug, savedPackage };
    } catch (err) {
      console.error('Error saving package:', err);
      if (isPartnerPlanLimitError(err)) {
        setPlanEntitlements(err.response.data.entitlements || null);
        const feature = err.response.data?.feature;
        if (feature === 'hasFeaturedPlacement') {
          setPlanWallReason('featured');
        } else if (feature === 'hasMarketplaceListing') {
          setPlanWallReason('marketplace');
        } else {
          setPlanWallReason('billing');
        }
        setPlanWallOpen(true);
      }
      setError(err.response?.data?.error || t('createPackage.saveFailed'));
      setSaveState('error');
      throw err;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [activePackageId, formData, getAuthHeaders, items, navigate, packageId, pricingTiers, t]);

  const goToReview = useCallback(async () => {
    if (isDirty) {
      try {
        const result = await handleSave(false);
        if (result?.savedSlug) setPreviewSlug(result.savedSlug);
      } catch (err) {
        console.warn('Auto-save before review failed:', err);
      }
    }
    setPreviewKey((key) => key + 1);
  }, [handleSave, isDirty]);

  const showStep = useCallback((nextStep) => {
    setShowHistory(false);
    setActiveStep(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleStepClick = async (stepIndex) => {
    if (stepIndex === activeStep && !showHistory) return;
    if (stepIndex === REVIEW_STEP) await goToReview();
    showStep(stepIndex);
  };

  const handleContinue = async () => {
    if (activeStep === REVIEW_STEP) {
      if (!ready) return;
      try {
        await handleSave(true);
      } catch (err) {
        // surfaced by handleSave
      }
      return;
    }
    const nextStep = Math.min(activeStep + 1, REVIEW_STEP);
    if (nextStep === REVIEW_STEP) await goToReview();
    showStep(nextStep);
  };

  const handlePreview = async () => {
    try {
      const result = await handleSave(false);
      const slug = result?.savedSlug || effectivePreviewSlug || formData.slug;
      if (slug) {
        setPreviewSlug(slug);
        if (activeStep === REVIEW_STEP) setPreviewKey((key) => key + 1);
        window.open(`/packages/${slug}?preview=1`, '_blank');
      } else {
        setError(t('createPackage.previewNeedsSave'));
      }
    } catch (err) {
      // surfaced by handleSave
    }
  };

  const handleAddItem = (dayNumber, { asAddOn = false } = {}) => {
    setItemEditorMode(asAddOn ? 'addon' : 'included');
    setEditingItem({
      day_number: dayNumber,
      name: '',
      location: '',
      google_map_link: '',
      category: 'activity',
      start_datetime: '',
      end_datetime: '',
      description: '',
      cost: asAddOn ? '' : 0,
      cost_currency: formData.currency || 'USD',
      transportation_type: '',
      transportation_data: {},
      is_included: !asAddOn,
      image_url: '',
    });
    setEditingItemIndex(-1);
    setItemDialogOpen(true);
  };

  const handleEditItem = (item, index, { asAddOn = !isIncludedItem(item) } = {}) => {
    setItemEditorMode(asAddOn ? 'addon' : 'included');
    setEditingItem({
      ...item,
      is_included: !asAddOn,
      cost: asAddOn ? (item.additional_cost || item.cost || '') : (item.cost || ''),
    });
    setEditingItemIndex(index);
    setItemDialogOpen(true);
  };

  const handleOfferAsAddOn = (item, index) => {
    handleEditItem(item, index, { asAddOn: true });
  };

  const handleIncludeInTrip = (index) => {
    setItems((prev) => prev.map((item, i) => (
      i === index ? { ...item, is_included: true, additional_cost: 0 } : item
    )));
  };

  const openInventoryPicker = async () => {
    setInventoryOpen(true);
    setInventoryId('');
    setInventoryPricingMode('charge');
    try {
      const response = await api.get('/partner-inventory/items', PARTNER_AUTH);
      setInventoryItems((response.data.items || []).filter((item) => item.active !== false));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerInventory.loadFailed'));
      setInventoryItems([]);
    }
  };

  const addInventoryAsAddOn = async () => {
    const selected = inventoryItems.find((item) => String(item.id) === String(inventoryId));
    if (!selected) return;
    const free = inventoryPricingMode === 'free';
    const price = Number(selected.defaultPrice ?? selected.default_price ?? 0);
    if (!free && !(price > 0)) {
      setError(t('createPackage.inventoryNeedsPrice'));
      return;
    }
    try {
      setInventoryBusy(true);
      setError('');
      const dayNumber = Number(addOnDay) || 1;
      if (activePackageId) {
        await api.post(
          `/partner-inventory/items/${selected.id}/attach-to-package`,
          { packageId: Number(activePackageId), dayNumber, free },
          PARTNER_AUTH
        );
        await fetchPackage(activePackageId);
      } else {
        const freq = normalizeInventoryPriceFrequency(selected.priceFrequency ?? selected.price_frequency);
        const freqLabel = freq === 'per_day' ? 'per day' : freq === 'per_week' ? 'per week' : null;
        const draftName = free
          ? (freqLabel ? `${selected.name} (${freqLabel} · complimentary)` : `${selected.name} (complimentary)`)
          : (freqLabel ? `${selected.name} (${freqLabel})` : selected.name);
        setItems((prev) => ([
          ...prev,
          {
            day_number: dayNumber,
            name: draftName,
            location: '',
            description: selected.description || '',
            category: inventoryCategoryToPackage(selected.category),
            cost: 0,
            additional_cost: free ? 0 : price,
            cost_currency: selected.currency || formData.currency || 'USD',
            is_included: false,
            image_url: selected.imageUrl || selected.image_url || '',
            sort_order: prev.length,
          },
        ]));
      }
      setInventoryOpen(false);
      setInventoryId('');
      setInventoryPricingMode('charge');
    } catch (err) {
      setError(err.response?.data?.error || t('createPackage.inventoryAttachFailed'));
    } finally {
      setInventoryBusy(false);
    }
  };

  const handleAddOnDayChange = (index, dayNumber) => {
    setItems((prev) => prev.map((item, i) => (
      i === index ? { ...item, day_number: Number(dayNumber) || 1 } : item
    )));
  };

  const handleSaveItem = (itemData) => {
    setItems((prev) => {
      const asAddOn = itemEditorMode === 'addon';
      const extra = Number(itemData.additional_cost ?? itemData.cost ?? 0);
      const newItem = {
        ...itemData,
        day_number: Number(itemData.day_number || editingItem?.day_number || addOnDay || 1),
        is_included: !asAddOn,
        additional_cost: asAddOn ? extra : 0,
        sort_order: editingItemIndex >= 0 ? prev[editingItemIndex]?.sort_order : prev.length,
        image_url: itemData.image_url
          || (editingItemIndex >= 0 ? (prev[editingItemIndex]?.image_url || '') : (editingItem?.image_url || '')),
      };
      const next = [...prev];
      if (editingItemIndex >= 0) next[editingItemIndex] = newItem;
      else next.push(newItem);
      return next;
    });
    setItemDialogOpen(false);
    setEditingItem(null);
  };

  const handleDeleteItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemImageChange = useCallback((itemId, globalIndex, url) => {
    setItems((prev) => {
      const next = [...prev];
      let idx = itemId ? prev.findIndex((item) => item.id === itemId) : -1;
      if (idx === -1 && globalIndex >= 0 && globalIndex < prev.length) idx = globalIndex;
      if (idx >= 0) next[idx] = { ...next[idx], image_url: url };
      return next;
    });
  }, []);

  const handlePricingTierChange = (index, field, value) => {
    setPricingTiers((prev) => prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)));
    if (field === 'price_per_person' && index === 0) {
      setFormData((prev) => ({ ...prev, base_price: value }));
    }
  };

  const itemIndex = (item) => {
    const byRef = items.findIndex((candidate) => candidate === item);
    if (byRef >= 0) return byRef;
    return items.findIndex((candidate) => (
      (candidate.id && candidate.id === item.id)
      || (candidate.name === item.name && candidate.day_number === item.day_number && candidate.sort_order === item.sort_order)
    ));
  };

  const originalPrice = parseFloat(formData.original_price) || 0;
  const discountHint = originalPrice > 0 && price > 0 && originalPrice > price
    ? t('createPackage.discountHint', {
      percent: Math.round((1 - price / originalPrice) * 100),
      amount: formatMoney(originalPrice - price, formData.currency),
    })
    : '';

  const checks = [
    { ok: Boolean(formData.title.trim()), label: t('createPackage.checkName'), step: 0 },
    { ok: Boolean(formData.destination.trim()), label: t('createPackage.checkDest'), step: 0 },
    { ok: includedItems.length > 0, label: t('createPackage.checkItinerary'), step: 1 },
    { ok: price > 0, label: t('createPackage.checkPrice'), step: 2 },
    { ok: includes.length > 0, label: t('createPackage.checkIncludes'), step: 2 },
  ];

  const stepMeta = [
    {
      title: t('createPackage.stepBasics'),
      status: basicsMissing
        ? t('createPackage.toComplete', { count: basicsMissing })
        : t('createPackage.complete'),
      ok: basicsMissing === 0,
    },
    {
      title: t('createPackage.stepItinerary'),
      status: includedItems.length
        ? t('createPackage.activitiesCount', { count: includedItems.length })
        : t('createPackage.daysProgress', { filled: 0, days }),
      ok: includedItems.length > 0,
    },
    {
      title: t('createPackage.stepPricing'),
      status: price > 0 ? t('createPackage.complete') : t('createPackage.toComplete', { count: 1 }),
      ok: price > 0,
    },
    {
      title: t('createPackage.stepAddOns'),
      status: addOnItems.length
        ? t('createPackage.addOnsCount', { count: addOnItems.length })
        : t('createPackage.addOnsOptional'),
      ok: true,
    },
    {
      title: t('createPackage.stepReview'),
      status: ready ? t('createPackage.readyToPublish') : t('createPackage.notReady'),
      ok: ready,
    },
  ];

  const barStatus = activeStep === REVIEW_STEP
    ? (ready ? t('createPackage.everythingOk') : t('createPackage.resolveToPublish'))
    : (activeStep === 0 && basicsMissing > 0 ? t('createPackage.needNameDest') : '');

  if (loading) {
    return (
      <Box sx={{ bgcolor: DOCUMENT.canvas, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        <Typography sx={{ mt: 2, color: DOCUMENT.ink2 }}>{t('createPackage.loading')}</Typography>
      </Box>
    );
  }

  return (
    <PartnerPageShell>
        <Typography sx={{ pt: { xs: 2, md: 1 }, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          <Box component={RouterLink} to="/partner/packages" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: DOCUMENT.ink } }}>
            {t('createPackage.crumbPackages')}
          </Box>
          {' · '}
          {isEditing ? t('createPackage.crumbEdit') : t('createPackage.crumbNew')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2.25, flexWrap: 'wrap', mt: 1.5 }}>
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 40 }, fontWeight: 400, lineHeight: 1 }}>
            {isEditing ? t('createPackage.editTitle') : t('createPackage.title')}
          </Typography>
          <Typography sx={{ ml: { sm: 'auto' }, fontSize: 11.5, color: DOCUMENT.ink3, pb: 0.75 }}>
            {saveState === 'saving' ? t('createPackage.saving') : t('createPackage.draftSaved')}
          </Typography>
        </Box>

        <Box
          role="tablist"
          aria-label={t('createPackage.steps')}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${isEditing ? 6 : 5}, minmax(0, 1fr))` },
            mt: 3.25,
            borderTop: `1px solid ${DOCUMENT.ruleHard}`,
            borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
          }}
        >
          {stepMeta.map((step, index) => {
            const current = !showHistory && activeStep === index;
            return (
              <Box
                key={step.title}
                component="button"
                type="button"
                role="tab"
                aria-selected={current}
                aria-current={current || undefined}
                onClick={() => handleStepClick(index)}
                sx={{
                  background: current ? DOCUMENT.surface : 'none',
                  border: 0,
                  borderRight: {
                    xs: index % 2 === 0 ? `1px solid ${DOCUMENT.rule}` : 0,
                    md: index < REVIEW_STEP || isEditing ? `1px solid ${DOCUMENT.rule}` : 0,
                  },
                  borderBottom: { xs: index < 4 ? `1px solid ${DOCUMENT.rule}` : 0, md: 0 },
                  font: 'inherit',
                  textAlign: 'left',
                  px: 2.25,
                  pt: 1.75,
                  pb: 1.6,
                  cursor: 'pointer',
                  color: DOCUMENT.ink3,
                }}
              >
                <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: current ? DOCUMENT.accent : DOCUMENT.ink3, display: 'block', mb: 0.75 }}>
                  {t('createPackage.stepN', { n: index + 1 })}
                </Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 19, color: current ? DOCUMENT.ink : DOCUMENT.ink2, display: 'block', lineHeight: 1.1 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: 11.5, display: 'block', mt: 0.75, color: step.ok ? DOCUMENT.accent : '#8C2B22' }}>
                  {step.status}
                </Typography>
              </Box>
            );
          })}
          {isEditing && (
            <Box
              component="button"
              type="button"
              role="tab"
              aria-selected={showHistory}
              aria-current={showHistory || undefined}
              data-testid="package-editor-history-tab"
              onClick={() => {
                setShowHistory(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              sx={{
                background: showHistory ? DOCUMENT.surface : 'none',
                border: 0,
                font: 'inherit',
                textAlign: 'left',
                px: 2.25,
                pt: 1.75,
                pb: 1.6,
                cursor: 'pointer',
                color: DOCUMENT.ink3,
              }}
            >
              <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: showHistory ? DOCUMENT.accent : DOCUMENT.ink3, display: 'block', mb: 0.75 }}>
                {t('changeHistory.logLabel')}
              </Typography>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 19, color: showHistory ? DOCUMENT.ink : DOCUMENT.ink2, display: 'block', lineHeight: 1.1 }}>
                {t('changeHistory.historyTab')}
              </Typography>
              <Typography sx={{ fontSize: 11.5, display: 'block', mt: 0.75, color: DOCUMENT.ink3 }}>
                {t('changeHistory.whoChangedWhat')}
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!showHistory && activeStep === 0 && (
          <Box sx={{ pt: 4.25 }}>
            <Legend title={t('createPackage.basics')} sub={t('createPackage.basicsSub')} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25 }}>
              <Field label={t('createPackage.name')} required wide>
                <TextField
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  variant="standard"
                  placeholder={t('createPackage.namePlaceholder')}
                  inputProps={{ 'aria-label': t('createPackage.name') }}
                  sx={underlineSx}
                />
              </Field>
              <Field label={t('createPackage.destination')} required wide>
                <LocationAutocomplete
                  value={formData.destination}
                  onChange={(value) => setFormData((prev) => ({ ...prev, destination: value }))}
                  onPlaceSelect={(place) => {
                    const addr = place.address || place.name || '';
                    const parts = addr.split(',').map((part) => part.trim());
                    setFormData((prev) => ({
                      ...prev,
                      destination: addr,
                      destination_country: parts.length > 1 ? parts[parts.length - 1] : prev.destination_country,
                    }));
                  }}
                  label=""
                  variant="standard"
                  placeholder={t('createPackage.destinationPlaceholder')}
                  sx={locationSx}
                />
              </Field>
              <Field label={t('createPackage.startsIn')}>
                <LocationAutocomplete
                  value={formData.start_location}
                  onChange={(value) => setFormData((prev) => ({ ...prev, start_location: value }))}
                  onPlaceSelect={(place) => setFormData((prev) => ({ ...prev, start_location: place.address || place.name || '' }))}
                  label=""
                  variant="standard"
                  placeholder={t('createPackage.startsPlaceholder')}
                  sx={locationSx}
                />
              </Field>
              <Field label={t('createPackage.endsIn')} hint={t('createPackage.endsHint')}>
                <LocationAutocomplete
                  value={formData.end_location}
                  onChange={(value) => setFormData((prev) => ({ ...prev, end_location: value }))}
                  onPlaceSelect={(place) => setFormData((prev) => ({ ...prev, end_location: place.address || place.name || '' }))}
                  label=""
                  variant="standard"
                  placeholder={t('createPackage.endsPlaceholder')}
                  sx={locationSx}
                />
              </Field>
              <Field label={t('createPackage.duration')} required hint={t('createPackage.nights', { count: nights })} live>
                <TextField
                  name="duration_days"
                  type="number"
                  value={formData.duration_days}
                  onChange={handleChange}
                  fullWidth
                  variant="standard"
                  inputProps={{ min: 1, max: 60, 'aria-label': t('createPackage.duration') }}
                  sx={underlineSx}
                />
              </Field>
              <Field
                label={t('createPackage.style')}
                hint={t(`createPackage.${PRICING_TYPES.find((type) => type.value === formData.pricing_type)?.hintKey || 'styleHintLand'}`)}
              >
                <TextField
                  select
                  name="pricing_type"
                  value={formData.pricing_type}
                  onChange={handleChange}
                  fullWidth
                  variant="standard"
                  inputProps={{ 'aria-label': t('createPackage.style') }}
                  sx={underlineSx}
                >
                  {PRICING_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{t(`createPackage.styles.${type.value}`)}</MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field
                label={t('createPackage.shortDesc')}
                wide
                hint={t('createPackage.charCount', { count: formData.short_description.length, max: SHORT_MAX })}
              >
                <TextField
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="standard"
                  placeholder={t('createPackage.shortPlaceholder')}
                  inputProps={{ maxLength: SHORT_MAX, 'aria-label': t('createPackage.shortDesc') }}
                  sx={underlineSx}
                />
              </Field>
              <Field label={t('createPackage.cover')} wide hint={t('createPackage.coverHint')}>
                <SingleImageUploader
                  value={formData.cover_image}
                  onChange={(url) => setFormData((prev) => ({ ...prev, cover_image: url || '' }))}
                  preset="cover"
                  folder="packages/covers"
                  label={t('createPackage.coverBrowse')}
                  height={140}
                  cropShape="rect"
                />
              </Field>
            </Box>

            <Box
              component="button"
              type="button"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
              sx={{
                ...ghostSx,
                mt: 3,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                px: 2.25,
                py: 1.35,
                '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
              }}
            >
              {moreOpen ? t('createPackage.hideDetails') : t('createPackage.moreDetails')}
            </Box>
            {moreOpen && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25, mt: 1 }}>
                <Field label={t('createPackage.tripCode')} hint={t('createPackage.tripCodeHint')}>
                  <TextField
                    name="trip_code"
                    value={formData.trip_code}
                    onChange={(event) => setFormData((prev) => ({
                      ...prev,
                      trip_code: event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''),
                    }))}
                    fullWidth
                    variant="standard"
                    placeholder="BALI-7D"
                    inputProps={{ maxLength: 20, 'aria-label': t('createPackage.tripCode') }}
                    sx={underlineSx}
                  />
                </Field>
                <Field label={t('createPackage.packageType')}>
                  <TextField
                    select
                    name="package_type"
                    value={formData.package_type}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    sx={underlineSx}
                  >
                    {PACKAGE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>{t(`createPackage.types.${type.value}`)}</MenuItem>
                    ))}
                  </TextField>
                </Field>
                <Field label={t('createPackage.fullDescription')} wide>
                  <TextField
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={3}
                    variant="standard"
                    placeholder={t('createPackage.fullDescriptionPlaceholder')}
                    sx={underlineSx}
                  />
                </Field>
                <Field label={t('createPackage.gallery')} wide>
                  <MultiImageUploader
                    value={formData.gallery}
                    onChange={(urls) => setFormData((prev) => ({ ...prev, gallery: urls }))}
                    preset="gallery"
                    folder="packages/gallery"
                    label={t('createPackage.gallery')}
                    maxImages={20}
                  />
                </Field>
                <Field label={t('createPackage.featured')} wide>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={formData.is_featured}
                        disabled={!canUseFeaturedPlacement(entitlements)}
                        onChange={(event) => {
                          if (event.target.checked && !canUseFeaturedPlacement(entitlements)) {
                            setPlanWallReason('featured');
                            setPlanWallOpen(true);
                            return;
                          }
                          setFormData((prev) => ({ ...prev, is_featured: event.target.checked }));
                        }}
                      />
                    )}
                    label={t('createPackage.featuredHint')}
                  />
                </Field>
                <Field label={t('createPackage.customizable')} wide>
                  <FormControlLabel
                    control={(
                      <Switch
                        name="is_customizable"
                        checked={formData.is_customizable}
                        onChange={(event) => setFormData((prev) => ({ ...prev, is_customizable: event.target.checked }))}
                      />
                    )}
                    label={t('createPackage.customizableHint')}
                  />
                </Field>
              </Box>
            )}
          </Box>
        )}

        {!showHistory && activeStep === 1 && (
          <Box sx={{ pt: 4.25 }}>
            <Legend title={t('createPackage.itinerary')} sub={t('createPackage.itinerarySub')} />
            {Array.from({ length: days }, (_, index) => index + 1).map((day) => {
              const dayItems = itemsByDay[day] || [];
              const count = dayItems.length;
              return (
                <Box key={day} sx={{ borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '56px 1fr', sm: '64px 1fr auto' },
                      gap: 2.25,
                      alignItems: 'center',
                      py: 1.85,
                    }}
                  >
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                      {t('createPackage.dayLabel', { n: String(day).padStart(2, '0') })}
                    </Typography>
                    <Box>
                      <Typography sx={{ fontSize: 15, color: count ? DOCUMENT.ink : DOCUMENT.placeholder }}>
                        {count
                          ? t(count === 1 ? 'createPackage.activityOne' : 'createPackage.activityMany', { count })
                          : t('createPackage.nothingPlanned')}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                        {count ? t('createPackage.tapToEdit') : t('createPackage.addPrompt')}
                      </Typography>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => handleAddItem(day)}
                      sx={{
                        ...ghostSx,
                        border: `1px solid ${DOCUMENT.ruleHard}`,
                        px: 1.75,
                        py: 1,
                        letterSpacing: '0.11em',
                        alignSelf: { xs: 'start', sm: 'center' },
                        gridColumn: { xs: '2', sm: 'auto' },
                        justifySelf: { xs: 'start', sm: 'end' },
                        '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
                      }}
                    >
                      {count ? t('createPackage.edit') : t('createPackage.add')}
                    </Box>
                  </Box>
                  {dayItems.map((item) => {
                    const index = itemIndex(item);
                    const categoryInfo = ITEM_CATEGORIES.find((category) => category.value === item.category);
                    return (
                      <Box key={item.id || `${item.day_number}-${item.name}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1.75, pl: { xs: 0, sm: 10 } }}>
                        <Box sx={{ color: DOCUMENT.oxblood, mt: 0.25 }}>{categoryInfo?.icon}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 15 }}>{item.name || t('createPackage.untitledActivity')}</Typography>
                          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.4 }}>
                            {[item.location, item.transportation_type, item.start_datetime && new Date(item.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })].filter(Boolean).join(' · ')}
                          </Typography>
                          {item.description && (
                            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.75, whiteSpace: 'pre-line' }}>{item.description}</Typography>
                          )}
                          {item.image_url ? (
                            <Box sx={{ mt: 1.25, position: 'relative', width: 'fit-content' }}>
                              <Box component="img" src={item.image_url} alt={item.name} sx={{ width: 180, height: 108, objectFit: 'cover', display: 'block' }} />
                              <Box
                                component="button"
                                type="button"
                                onClick={() => handleItemImageChange(item.id, index, '')}
                                sx={{ ...ghostSx, position: 'absolute', top: 6, right: 6, color: '#fff' }}
                              >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/jpeg,image/png,image/webp,image/gif';
                                input.onchange = async (event) => {
                                  const file = event.target.files[0];
                                  if (!file) return;
                                  try {
                                    const fd = new FormData();
                                    fd.append('image', file);
                                    const res = await api.post(
                                      '/uploads/image?preset=itinerary&folder=packages/itinerary',
                                      fd,
                                      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
                                    );
                                    if (res.data.success) handleItemImageChange(item.id, index, res.data.url);
                                  } catch (err) {
                                    console.error('Item image upload failed:', err);
                                  }
                                };
                                input.click();
                              }}
                              sx={{ ...ghostSx, mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                            >
                              <AddPhotoIcon sx={{ fontSize: 14 }} />
                              {t('createPackage.addPhoto')}
                            </Box>
                          )}
                        </Box>
                        <Box component="button" type="button" onClick={() => handleEditItem(item, index)} sx={ghostSx}>
                          {t('createPackage.edit')}
                        </Box>
                        <Box component="button" type="button" onClick={() => handleDeleteItem(index)} sx={{ ...ghostSx, '&:hover': { color: '#8C2B22', borderBottomColor: '#8C2B22' } }}>
                          {t('createPackage.remove')}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
            <Box sx={{ display: 'flex', gap: 3.25, flexWrap: 'wrap', py: 2, fontSize: 12.5, color: DOCUMENT.ink2 }}>
              <span><Box component="b" sx={{ fontWeight: 400, color: DOCUMENT.ink }}>{days}</Box> {t('createPackage.daysWord')}</span>
              <span><Box component="b" sx={{ fontWeight: 400, color: DOCUMENT.ink }}>{nights}</Box> {t('createPackage.nightsWord')}</span>
              <span><Box component="b" sx={{ fontWeight: 400, color: DOCUMENT.ink }}>{includedItems.length}</Box> {t('createPackage.activitiesWord')}</span>
              <span>{includedItems.length ? t('createPackage.readyToPrice') : t('createPackage.needActivity')}</span>
            </Box>
          </Box>
        )}

        {!showHistory && activeStep === 2 && (
          <Box sx={{ pt: 4.25 }}>
            <Legend title={t('createPackage.howPay')} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: '1px', bgcolor: DOCUMENT.ruleHard, border: `1px solid ${DOCUMENT.ruleHard}`, mt: 0.75 }}>
              {[
                { key: 'checkout', title: t('createPackage.direct'), body: t('createPackage.directHint') },
                { key: 'inquiry', title: t('createPackage.inquiry'), body: t('createPackage.inquiryHint') },
                { key: 'external', title: t('createPackage.external'), body: t('createPackage.externalHint') },
              ].map((option) => {
                const pressed = formData.booking_method === option.key;
                return (
                  <Box
                    key={option.key}
                    component="button"
                    type="button"
                    aria-pressed={pressed}
                    onClick={() => setFormData((prev) => ({
                      ...prev,
                      booking_method: prev.dmc_id ? 'inquiry' : option.key,
                    }))}
                    sx={{
                      background: pressed ? DOCUMENT.oxblood : DOCUMENT.surface,
                      color: pressed ? DOCUMENT.ivory : DOCUMENT.ink,
                      border: 0,
                      font: 'inherit',
                      textAlign: 'left',
                      px: 2.5,
                      py: 2.25,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 19, fontWeight: 400, mb: 0.75, color: 'inherit' }}>
                      {option.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: pressed ? '#E6D4C4' : DOCUMENT.ink2 }}>
                      {option.body}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ mt: 4.25 }}>
              <Legend title={t('createPackage.listing')} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '1px', bgcolor: DOCUMENT.ruleHard, border: `1px solid ${DOCUMENT.ruleHard}`, mt: 0.75 }}>
                {[
                  { key: 'marketplace', title: t('createPackage.listingMarketplace'), body: t('createPackage.listingMarketplaceHint') },
                  { key: 'private_client', title: t('createPackage.listingPrivate'), body: t('createPackage.listingPrivateHint') },
                ].map((option) => {
                  const pressed = formData.visibility === option.key;
                  const marketplaceLocked = option.key === 'marketplace' && !canUseMarketplaceListing(entitlements);
                  return (
                    <Box
                      key={option.key}
                      component="button"
                      type="button"
                      aria-pressed={pressed}
                      aria-disabled={marketplaceLocked}
                      onClick={() => {
                        if (marketplaceLocked) {
                          setPlanWallReason('marketplace');
                          setPlanWallOpen(true);
                          return;
                        }
                        setFormData((prev) => ({ ...prev, visibility: option.key }));
                      }}
                      sx={{
                        background: pressed ? DOCUMENT.oxblood : DOCUMENT.surface,
                        color: pressed ? DOCUMENT.ivory : DOCUMENT.ink,
                        border: 0,
                        font: 'inherit',
                        textAlign: 'left',
                        px: 2.5,
                        py: 2.25,
                        cursor: marketplaceLocked ? 'not-allowed' : 'pointer',
                        opacity: marketplaceLocked ? 0.65 : 1,
                      }}
                    >
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 19, fontWeight: 400, mb: 0.75, color: 'inherit' }}>
                        {option.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: pressed ? '#E6D4C4' : DOCUMENT.ink2 }}>
                        {option.body}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {privateTravelAvailable ? (
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(formData.is_private_journey)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          is_private_journey: checked,
                          booking_method: checked ? 'inquiry' : prev.booking_method,
                          visibility: checked ? 'marketplace' : prev.visibility,
                          pricing_kind: checked
                            ? (prev.pricing_kind || 'indicative_group_budget')
                            : '',
                        }));
                      }}
                    />
                  )}
                  label={t('createPackage.privateJourneyListing')}
                />
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5, maxWidth: 520 }}>
                  {t('createPackage.privateJourneyListingHint')}
                </Typography>
                {formData.is_private_journey ? (
                  <Box sx={{ mt: 2.5, display: 'grid', gap: 2, maxWidth: 560 }}>
                    <Alert severity="warning" sx={{ borderRadius: 0 }}>
                      {t('createPackage.privateJourneyBudgetWarn')}
                    </Alert>
                    <Field label={t('createPackage.pricingKind')} wide>
                      <TextField
                        select
                        name="pricing_kind"
                        value={formData.pricing_kind || 'indicative_group_budget'}
                        onChange={handleChange}
                        fullWidth
                        variant="standard"
                        sx={underlineSx}
                      >
                        <MenuItem value="indicative_group_budget">{t('createPackage.pricingKindGroupBudget')}</MenuItem>
                        <MenuItem value="request_quote">{t('createPackage.pricingKindRequestQuote')}</MenuItem>
                      </TextField>
                    </Field>
                    {formData.pricing_kind !== 'request_quote' ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <Field label={t('createPackage.budgetMin')}>
                          <TextField
                            name="budget_min"
                            type="number"
                            value={formData.budget_min}
                            onChange={handleChange}
                            fullWidth
                            variant="standard"
                            sx={underlineSx}
                            inputProps={{ min: 0, step: 1000, 'data-testid': 'private-budget-min' }}
                          />
                        </Field>
                        <Field label={t('createPackage.budgetMax')}>
                          <TextField
                            name="budget_max"
                            type="number"
                            value={formData.budget_max}
                            onChange={handleChange}
                            fullWidth
                            variant="standard"
                            sx={underlineSx}
                            inputProps={{ min: 0, step: 1000, 'data-testid': 'private-budget-max' }}
                          />
                        </Field>
                      </Box>
                    ) : null}
                    <Field label={t('createPackage.pricingAssumptions')} wide hint={t('createPackage.pricingAssumptionsHint')}>
                      <TextField
                        name="pricing_assumptions"
                        value={formData.pricing_assumptions}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        minRows={2}
                        variant="standard"
                        sx={underlineSx}
                      />
                    </Field>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            {formData.booking_method === 'checkout' && formData.visibility === 'private_client' && (
              <CommissionRow
                label={t('createPackage.commission')}
                value={t('createPackage.privateFee')}
              />
            )}
            {formData.booking_method === 'checkout' && formData.visibility !== 'private_client' && feeRate != null && (
              <>
                <CommissionRow
                  label={t('createPackage.commission')}
                  value={t('createPackage.commissionValue', { rate: feeRate, ...feeCopy })}
                />
                <CommissionRow
                  label={t('createPackage.youReceive', { amount: formatMoney(sampleBooking, formData.currency) })}
                  value={formatMoney(youReceive, formData.currency)}
                />
              </>
            )}
            <PartnerDmcField
              dmcId={formData.dmc_id}
              onChange={(nextId, extra = {}) => {
                setFormData((prev) => ({
                  ...prev,
                  dmc_id: nextId,
                  ...(extra.bookingMethod ? { booking_method: extra.bookingMethod } : {}),
                }));
              }}
            />
            {formData.booking_method === 'external' && (
              <Field label={t('createPackage.externalUrl')} wide hint={t('createPackage.externalUrlHint')}>
                <TextField
                  name="external_booking_url"
                  value={formData.external_booking_url}
                  onChange={handleChange}
                  fullWidth
                  variant="standard"
                  placeholder="https://your-website.com/book"
                  sx={underlineSx}
                />
              </Field>
            )}

            <Box sx={{ mt: 4.25 }}>
              <Legend title={t('createPackage.rates')} sub={t('createPackage.ratesSub')} />
              {pricingTiers.map((tier, index) => (
                <Box key={`${tier.tier_name}-${index}`} sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, p: 2.25, mt: 1.75, bgcolor: DOCUMENT.surface }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                      {t('createPackage.rateN', { n: index + 1 })}
                    </Typography>
                    {pricingTiers.length > 1 && (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setPricingTiers((prev) => prev.filter((_, i) => i !== index))}
                        sx={{ ...ghostSx, color: DOCUMENT.ink3, '&:hover': { color: '#8C2B22', borderBottomColor: '#8C2B22' } }}
                      >
                        {t('createPackage.remove')}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 130px 130px' }, columnGap: 2.5 }}>
                    <Field label={t('createPackage.rateName')} flush>
                      <TextField
                        value={tier.tier_name}
                        onChange={(event) => handlePricingTierChange(index, 'tier_name', event.target.value)}
                        fullWidth
                        variant="standard"
                        sx={underlineSx}
                      />
                    </Field>
                    <Field label={t('createPackage.perPerson')} flush>
                      <TextField
                        type="number"
                        value={tier.price_per_person}
                        onChange={(event) => handlePricingTierChange(index, 'price_per_person', event.target.value)}
                        fullWidth
                        variant="standard"
                        placeholder="0"
                        inputProps={{ min: 0, 'aria-label': t('createPackage.perPerson') }}
                        sx={underlineSx}
                      />
                    </Field>
                    <Field label={t('createPackage.room')} flush>
                      <TextField
                        select
                        value={tier.occupancy_type || 'double'}
                        onChange={(event) => handlePricingTierChange(index, 'occupancy_type', event.target.value)}
                        fullWidth
                        variant="standard"
                        sx={underlineSx}
                      >
                        {ROOM_TYPES.map((room) => (
                          <MenuItem key={room} value={room}>{t(`createPackage.rooms.${room}`)}</MenuItem>
                        ))}
                      </TextField>
                    </Field>
                    <Field label={t('createPackage.tierDescription')} wide flush>
                      <TextField
                        value={tier.description || ''}
                        onChange={(event) => handlePricingTierChange(index, 'description', event.target.value)}
                        fullWidth
                        variant="standard"
                        placeholder={t('createPackage.tierDescriptionPlaceholder')}
                        sx={underlineSx}
                      />
                    </Field>
                    {formData.booking_method === 'checkout' && (
                      <Field label={t('createPackage.stripeLink')} wide flush hint={t('createPackage.stripeHint')}>
                        <TextField
                          value={tier.stripe_payment_url || ''}
                          onChange={(event) => handlePricingTierChange(index, 'stripe_payment_url', event.target.value)}
                          fullWidth
                          variant="standard"
                          placeholder="https://buy.stripe.com/..."
                          sx={underlineSx}
                        />
                      </Field>
                    )}
                  </Box>
                </Box>
              ))}
              <Box
                component="button"
                type="button"
                onClick={() => setPricingTiers((prev) => [...prev, { tier_name: '', description: '', price_per_person: '', occupancy_type: 'double', stripe_payment_url: '' }])}
                sx={{
                  ...ghostSx,
                  mt: 2,
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                  px: 1.75,
                  py: 1,
                  '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
                }}
              >
                {t('createPackage.addRate')}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25, mt: 3.25 }}>
                <Field label={t('createPackage.originalPrice')} hint={discountHint} live>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 108px', gap: 2.25 }}>
                    <TextField
                      name="original_price"
                      type="number"
                      value={formData.original_price}
                      onChange={handleChange}
                      fullWidth
                      variant="standard"
                      placeholder="0"
                      inputProps={{ min: 0, 'aria-label': t('createPackage.originalPrice') }}
                      sx={underlineSx}
                    />
                    <TextField
                      select
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      variant="standard"
                      sx={underlineSx}
                    >
                      {CURRENCIES.filter((currency) => CURRENCY_CODES.includes(currency.code))
                        .concat(CURRENCY_CODES.includes('AED') ? [{ code: 'AED', name: 'UAE Dirham' }] : [])
                        .filter((currency, index, list) => list.findIndex((row) => row.code === currency.code) === index)
                        .map((currency) => (
                          <MenuItem key={currency.code} value={currency.code}>{currency.code}</MenuItem>
                        ))}
                    </TextField>
                  </Box>
                </Field>
                <Field label={t('createPackage.discountPercent')}>
                  <TextField
                    name="discount_percent"
                    type="number"
                    value={formData.discount_percent}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    inputProps={{ min: 0, max: 100, 'aria-label': t('createPackage.discountPercent') }}
                    sx={underlineSx}
                  />
                </Field>
                <Field label={t('createPackage.offerEnds')} hint={t('createPackage.offerHint')}>
                  <TextField
                    name="discount_valid_until"
                    type="date"
                    value={formData.discount_valid_until}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': t('createPackage.offerEnds') }}
                    sx={underlineSx}
                  />
                </Field>
              </Box>
            </Box>

            <Box sx={{ mt: 4.25 }}>
              <Legend title={t('createPackage.priceCovers')} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25 }}>
                <ListBuilder
                  label={t('createPackage.included')}
                  items={includes}
                  draft={includeDraft}
                  setDraft={setIncludeDraft}
                  onAdd={() => addListItem('price_includes', includeDraft, setIncludeDraft)}
                  onRemove={(index) => setList('price_includes', includes.filter((_, i) => i !== index))}
                  placeholder={t('createPackage.addItem')}
                  removeLabel={t('createPackage.remove')}
                />
                <ListBuilder
                  label={t('createPackage.notIncluded')}
                  items={excludes}
                  draft={excludeDraft}
                  setDraft={setExcludeDraft}
                  onAdd={() => addListItem('price_excludes', excludeDraft, setExcludeDraft)}
                  onRemove={(index) => setList('price_excludes', excludes.filter((_, i) => i !== index))}
                  placeholder={t('createPackage.addItem')}
                  removeLabel={t('createPackage.remove')}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 4.25 }}>
              <Legend title={t('createPackage.availability')} sub={t('createPackage.availabilitySub')} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25 }}>
                <Field label={t('createPackage.availableFrom')}>
                  <TextField name="available_from" type="date" value={formData.available_from} onChange={handleChange} fullWidth variant="standard" InputLabelProps={{ shrink: true }} sx={underlineSx} />
                </Field>
                <Field label={t('createPackage.availableUntil')}>
                  <TextField name="available_until" type="date" value={formData.available_until} onChange={handleChange} fullWidth variant="standard" InputLabelProps={{ shrink: true }} sx={underlineSx} />
                </Field>
                <Field label={t('createPackage.minTravelers')}>
                  <TextField name="min_travelers" type="number" value={formData.min_travelers} onChange={handleChange} fullWidth variant="standard" inputProps={{ min: 1 }} sx={underlineSx} />
                </Field>
                <Field label={t('createPackage.maxTravelers')}>
                  <TextField name="max_travelers" type="number" value={formData.max_travelers} onChange={handleChange} fullWidth variant="standard" inputProps={{ min: 1 }} sx={underlineSx} />
                </Field>
                <Field label={t('createPackage.bookingCloses')} wide hint={t('createPackage.bookingClosesHint')}>
                  <TextField name="booking_deadline_days" type="number" value={formData.booking_deadline_days} onChange={handleChange} fullWidth variant="standard" inputProps={{ min: 0 }} sx={underlineSx} />
                </Field>
                <Field
                  label={t('createPackage.cancellationPolicy')}
                  wide
                  hint={t(`createPackage.cancellationPolicies.${formData.cancellation_policy}.detail`)}
                >
                  <TextField
                    select
                    name="cancellation_policy"
                    value={formData.cancellation_policy}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    inputProps={{ 'aria-label': t('createPackage.cancellationPolicy') }}
                    sx={underlineSx}
                  >
                    {CANCELLATION_POLICY_KEYS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(`createPackage.cancellationPolicies.${key}.label`)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Field>
              </Box>
            </Box>
          </Box>
        )}

        {!showHistory && activeStep === ADDONS_STEP && (
          <Box sx={{ pt: 4.25 }} data-testid="package-addons-step">
            <Legend title={t('createPackage.addOns')} sub={t('createPackage.addOnsSub')} />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
              <TextField
                select
                value={addOnDay}
                onChange={(event) => setAddOnDay(Number(event.target.value) || 1)}
                variant="standard"
                inputProps={{ 'aria-label': t('createPackage.addOnDay') }}
                sx={{ ...underlineSx, minWidth: 120, mb: 0 }}
              >
                {Array.from({ length: days }, (_, index) => index + 1).map((day) => (
                  <MenuItem key={day} value={day}>
                    {t('createPackage.dayLabel', { n: String(day).padStart(2, '0') })}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                type="button"
                onClick={() => handleAddItem(addOnDay, { asAddOn: true })}
                sx={{
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  borderRadius: 0,
                  px: 2.75,
                  py: 1.5,
                  fontSize: 11,
                  letterSpacing: '0.11em',
                  textTransform: 'uppercase',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
                }}
              >
                {t('createPackage.addAddOn')}
              </Button>
              <Button
                type="button"
                onClick={openInventoryPicker}
                data-testid="package-addons-from-inventory"
                sx={{
                  ...partnerLinkSx,
                  borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
                  px: 0.5,
                  py: 1,
                }}
              >
                {t('createPackage.fromInventory')}
              </Button>
            </Box>

            {addOnItems.length === 0 ? (
              <Typography sx={{ color: DOCUMENT.ink3, mb: 4 }}>{t('createPackage.addOnsEmpty')}</Typography>
            ) : (
              <Box sx={{ mb: 4 }}>
                {addOnItems.map((item) => {
                  const index = itemIndex(item);
                  return (
                    <Box
                      key={item.id || `${item.day_number}-${item.name}-${index}`}
                      data-testid={`package-addon-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        py: 1.75,
                        borderBottom: `1px solid ${DOCUMENT.rule}`,
                        flexWrap: 'wrap',
                      }}
                    >
                      <TextField
                        select
                        value={item.day_number || 1}
                        onChange={(event) => handleAddOnDayChange(index, event.target.value)}
                        variant="standard"
                        sx={{ ...underlineSx, width: 92 }}
                      >
                        {Array.from({ length: days }, (_, dayIndex) => dayIndex + 1).map((day) => (
                          <MenuItem key={day} value={day}>
                            {t('createPackage.dayLabel', { n: String(day).padStart(2, '0') })}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box sx={{ flex: 1, minWidth: 180 }}>
                        <Typography sx={{ fontSize: 15 }}>{item.name || t('createPackage.untitledActivity')}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.4 }}>
                          {[item.location, item.description].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontVariantNumeric: 'tabular-nums', pt: 0.25 }}>
                        {formatMoney(item.additional_cost || item.cost || 0, item.cost_currency || formData.currency)}
                      </Typography>
                      <Box component="button" type="button" onClick={() => handleEditItem(item, index, { asAddOn: true })} sx={ghostSx}>
                        {t('createPackage.edit')}
                      </Box>
                      <Box component="button" type="button" onClick={() => handleIncludeInTrip(index)} sx={ghostSx}>
                        {t('createPackage.includeInTrip')}
                      </Box>
                      <Box component="button" type="button" onClick={() => handleDeleteItem(index)} sx={{ ...ghostSx, '&:hover': { color: '#8C2B22', borderBottomColor: '#8C2B22' } }}>
                        {t('createPackage.remove')}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {includedItems.length > 0 && (
              <Box>
                <Legend title={t('createPackage.addOnFromItinerary')} sub={t('createPackage.addOnFromItinerarySub')} />
                {includedItems.map((item) => {
                  const index = itemIndex(item);
                  return (
                    <Box
                      key={item.id || `included-${item.day_number}-${item.name}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.4,
                        borderBottom: `1px solid ${DOCUMENT.rule}`,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.ink3, width: 64 }}>
                        {t('createPackage.dayLabel', { n: String(item.day_number || 1).padStart(2, '0') })}
                      </Typography>
                      <Typography sx={{ flex: 1, fontSize: 15 }}>{item.name || t('createPackage.untitledActivity')}</Typography>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleOfferAsAddOn(item, index)}
                        sx={ghostSx}
                      >
                        {t('createPackage.offerAsAddOn')}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {!showHistory && activeStep === REVIEW_STEP && (
          <Box sx={{ pt: 4.25 }}>
            <Legend title={t('createPackage.beforePublish')} />
            <Box sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface, mb: 3.25 }}>
              {checks.map((check) => (
                <Box
                  key={check.label}
                  sx={{
                    display: 'flex',
                    gap: 1.75,
                    alignItems: 'baseline',
                    px: 2.25,
                    py: 1.6,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                    '&:last-child': { borderBottom: 0 },
                    fontSize: 13.5,
                  }}
                >
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', width: 74, flexShrink: 0, color: check.ok ? DOCUMENT.accent : '#8C2B22' }}>
                    {check.ok ? t('createPackage.done') : t('createPackage.needed')}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5 }}>{check.label}</Typography>
                  {!check.ok && (
                    <Box component="button" type="button" onClick={() => showStep(check.step)} sx={{ ...ghostSx, ml: 'auto', whiteSpace: 'nowrap' }}>
                      {t('createPackage.goToStep', { n: check.step + 1 })}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            <Legend title={t('createPackage.customersSee')} />
            <Box data-testid="package-preview" sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface }}>
              {effectivePreviewSlug ? (
                <>
                  <Box sx={{ px: 2.25, py: 1.5, borderBottom: `1px solid ${DOCUMENT.rule}`, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                      dejavunomad.com/packages/{effectivePreviewSlug}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={handlePreview}
                      disabled={saving || !formData.title}
                      sx={ghostSx}
                    >
                      {t('createPackage.openPreview')}
                    </Box>
                  </Box>
                  <Box
                    key={previewKey}
                    component="iframe"
                    src={`/packages/${effectivePreviewSlug}?preview=1&t=${previewKey}`}
                    title={t('createPackage.livePreview')}
                    sx={{ width: '100%', height: 700, border: 0, display: 'block' }}
                  />
                </>
              ) : (
                <Typography sx={{ px: 2.25, py: 2.5, fontSize: 13.5, color: DOCUMENT.ink2 }}>
                  {t('createPackage.previewNeedsSave')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {showHistory && activePackageId && (
          <Box sx={{ pt: 4.25, pb: 4 }}>
            <ChangeHistoryPanel
              endpoint={`/partner-packages/packages/${activePackageId}/history`}
              requestConfig={PARTNER_AUTH}
              subtitle={t('changeHistory.packageSubtitle')}
              testId="package-editor-history"
            />
          </Box>
        )}

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: DOCUMENT.canvas,
            borderTop: `1px solid ${DOCUMENT.ruleHard}`,
            mt: 2.75,
            pt: 1.85,
            pb: 2.75,
            display: showHistory ? 'none' : 'flex',
            alignItems: 'center',
            gap: 2.25,
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => (activeStep === 0 ? navigate('/partner/packages') : showStep(activeStep - 1))}
            sx={{ ...ghostSx, visibility: activeStep === 0 ? 'hidden' : 'visible' }}
          >
            {t('createPackage.back')}
          </Box>
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>{barStatus}</Typography>
          <Box
            component="button"
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving}
            sx={{ ...ghostSx, ml: { sm: 'auto' } }}
          >
            {t('createPackage.saveDraft')}
          </Box>
          <Button
            onClick={handleContinue}
            disabled={(activeStep === 0 && basicsMissing > 0) || (activeStep === REVIEW_STEP && !ready) || saving}
            sx={{
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              borderRadius: 0,
              px: 2.75,
              py: 1.5,
              fontSize: 11,
              letterSpacing: '0.11em',
              textTransform: 'uppercase',
              '&:hover': { bgcolor: DOCUMENT.nav },
              '&.Mui-disabled': { bgcolor: DOCUMENT.ruleHard, color: '#fff' },
            }}
          >
            {saving && activeStep === REVIEW_STEP
              ? t('createPackage.saving')
              : activeStep === REVIEW_STEP
                ? t('createPackage.publish')
                : t('createPackage.continue')}
          </Button>
        </Box>

      <ActivityModal
        open={itemDialogOpen}
        onClose={() => {
          setItemDialogOpen(false);
          setEditingItem(null);
        }}
        onSubmit={handleSaveItem}
        mode={editingItemIndex >= 0 ? 'edit' : 'create'}
        initialData={editingItem || {}}
        tripMembers={[]}
        showVisibility={false}
        showCost={itemEditorMode === 'addon'}
        showPackageAddOn={itemEditorMode === 'addon'}
        defaultVisibility="everyone"
      />

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        entitlements={planEntitlements || entitlements}
        reason={planWallReason}
      />

      <PartnerInventoryPickerDialog
        open={inventoryOpen}
        onClose={() => {
          setInventoryOpen(false);
          setInventoryPricingMode('charge');
        }}
        items={inventoryItems}
        selectedId={inventoryId}
        onSelect={setInventoryId}
        onConfirm={addInventoryAsAddOn}
        confirming={inventoryBusy}
        title={t('createPackage.offerInventory')}
        confirmLabel={inventoryId
          ? t('createPackage.addToPackage')
          : t('createPackage.fromInventory')}
        emptyLabel={t('createPackage.inventoryEmpty')}
        pricingMode={inventoryPricingMode}
        onPricingModeChange={setInventoryPricingMode}
        testIdPrefix="package-addons-inventory"
      />
    </PartnerPageShell>
  );
};

const Legend = ({ title, sub }) => (
  <Typography
    component="h2"
    sx={{
      ...documentSubSectionTitleSx,
      pb: 1.35,
      borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
      mb: 0.5,
    }}
  >
    {title}
    {sub && (
      <Box component="span" sx={{ fontFamily: DOCUMENT.sans, fontSize: 12.5, color: DOCUMENT.ink3, ml: 1.5 }}>
        {sub}
      </Box>
    )}
  </Typography>
);

const Field = ({ label, required, wide, hint, live, flush, children }) => (
  <Box sx={{ py: flush ? 1.25 : 2.1, borderBottom: flush ? 0 : `1px solid ${DOCUMENT.rule}`, gridColumn: wide ? '1 / -1' : 'auto' }}>
    <Typography sx={documentFormLabelSx}>
      {label}
      {required && <Box component="span" sx={{ color: DOCUMENT.accent, ml: 0.4 }}>*</Box>}
    </Typography>
    {children}
    {hint ? (
      <Typography sx={{ fontSize: 12, color: live ? DOCUMENT.accent : DOCUMENT.ink3, mt: 1, lineHeight: 1.55 }}>{hint}</Typography>
    ) : null}
  </Box>
);

const CommissionRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.75, borderBottom: `1px solid ${DOCUMENT.rule}`, fontSize: 13 }}>
    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

const ListBuilder = ({ label, items, draft, setDraft, onAdd, onRemove, placeholder, removeLabel }) => (
  <Field label={label}>
    {items.map((item, index) => (
      <Box key={`${item}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: `1px solid ${DOCUMENT.rule}`, fontSize: 14 }}>
        <Typography sx={{ fontSize: 14, flex: 1 }}>{item}</Typography>
        <Box
          component="button"
          type="button"
          onClick={() => onRemove(index)}
          sx={{ ...ghostSx, ml: 'auto', color: DOCUMENT.ink3, '&:hover': { color: '#8C2B22', borderBottomColor: '#8C2B22' } }}
        >
          {removeLabel}
        </Box>
      </Box>
    ))}
    <TextField
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onAdd();
        }
      }}
      fullWidth
      variant="standard"
      placeholder={placeholder}
      sx={{ ...underlineSx, mt: 1.5 }}
    />
  </Field>
);

export default PartnerPackageEditor;
